import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, Building2, Users, Lock, Search } from 'lucide-react';
import {
  fetchUserAssignments, updateUserAssignments,
  fetchMetaHospitals, fetchMetaZones, fetchMetaSalesReps,
  fetchUserHospitals,
} from '../services/authService';

export default function UserAssignmentModal({ user, onClose, onSaved }) {
  // zones = selected zone badges (saved to DB + used as filter)
  const [zones,        setZones]        = useState([]);
  const [hospitals,    setHospitals]    = useState([]);   // own hospitals
  const [repHospitals, setRepHospitals] = useState({});   // { repId: [{name,zone}] }
  const [reports,      setReports]      = useState([]);   // ZBM only

  const [allHospitals, setAllHospitals] = useState([]);
  const [allZones,     setAllZones]     = useState([]);
  const [allSalesReps, setAllSalesReps] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [hospitalSearch, setHospitalSearch] = useState('');

  const isSales     = user.role === 'sales';
  const isPhysician = user.role === 'physician';
  const isZBM       = user.sub_type === 'ZBM';

  // Rep-sourced hospital names
  const repSourcedNames = new Set(
    Object.values(repHospitals).flat().map(h => h.name)
  );

  // All selected = own + rep-sourced
  const allSelectedNames = new Set([
    ...hospitals.map(h => h.name),
    ...repSourcedNames,
  ]);

  // Hospitals filtered by selected zones + search term
  // For Sales: zones empty = show nothing (must select a zone first)
  // For Physician: zones empty = show all (no zone concept)
  const filteredHospitalsByZone = Object.entries(
    allHospitals.reduce((acc, h) => {
      if (!acc[h.zone]) acc[h.zone] = [];
      acc[h.zone].push(h);
      return acc;
    }, {})
  )
    .filter(([zone]) => {
      if (isSales) {
        // Sales: must have at least one zone selected to show hospitals
        // ZBM with "All Zones" (zones=[]) shows all
        if (isZBM && zones.length === 0) return true;
        // Sales Rep / Medical Advisor: must pick specific zone
        return zones.includes(zone);
      }
      // Physician: show all zones always
      return true;
    })
    .map(([zone, hosps]) => [
      zone,
      // Apply search filter for physician
      isPhysician && hospitalSearch
        ? hosps.filter(h => h.name.toLowerCase().includes(hospitalSearch.toLowerCase()))
        : hosps,
    ])
    .filter(([, hosps]) => hosps.length > 0);

  // Load
  useEffect(() => {
    async function load() {
      try {
        const [assignments, hosps, zns, reps] = await Promise.all([
          fetchUserAssignments(user.id),
          fetchMetaHospitals(),
          fetchMetaZones(),
          isZBM ? fetchMetaSalesReps() : Promise.resolve([]),
        ]);

        const assignedZones = assignments.zones || [];
        setZones(assignedZones);
        setHospitals(assignments.hospitals || []);

        const currentReports = (assignments.reports || []).map(r => r.id);
        setReports(currentReports);

        setAllHospitals(hosps);
        setAllZones((zns || []).filter(z => z !== 'International'));
        setAllSalesReps(reps.filter(r => r.id !== user.id));

        // Load rep hospitals
        if (isZBM && currentReports.length > 0) {
          const repHospMap = {};
          await Promise.all(currentReports.map(async (repId) => {
            try {
              const h = await fetchUserHospitals(repId);
              repHospMap[repId] = h;
            } catch { repHospMap[repId] = []; }
          }));
          setRepHospitals(repHospMap);
        }
      } catch {
        setError('Failed to load assignments');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [user.id, isZBM]);

  // Toggle zone — saves assignment AND filters hospital list
  const toggleZone = (zone) => {
    setZones(prev =>
      prev.includes(zone) ? prev.filter(z => z !== zone) : [...prev, zone]
    );
  };

  const toggleAllZones = () => setZones([]); // empty = All Zones (physician only)

  const toggleHospital = (hosp) => {
    setHospitals(prev => {
      const exists = prev.find(h => h.name === hosp.name);
      return exists ? prev.filter(h => h.name !== hosp.name) : [...prev, hosp];
    });
  };

  const toggleReport = async (repId) => {
    const isAdding = !reports.includes(repId);
    setReports(prev => isAdding ? [...prev, repId] : prev.filter(id => id !== repId));
    if (isAdding) {
      try {
        const h = await fetchUserHospitals(repId);
        setRepHospitals(prev => ({ ...prev, [repId]: h }));
      } catch { /* silent */ }
    } else {
      setRepHospitals(prev => { const u = { ...prev }; delete u[repId]; return u; });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {};
      if (isSales)                payload.zones     = zones;
      if (isSales || isPhysician) payload.hospitals = hospitals;
      if (isZBM)                  payload.reports   = reports;
      await updateUserAssignments(user.id, payload);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 999999 }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col"
           onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Manage Assignments</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {user.name} · <span className="capitalize">{user.role}</span>
              {user.sub_type && ` · ${user.sub_type}`}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* ── Sales Reps Reporting — ZBM only ────────────────────── */}
              {isZBM && allSalesReps.length > 0 && (
                <div>
                  <div className="flex items-center space-x-2 mb-3">
                    <Users className="w-4 h-4 text-indigo-500" />
                    <h3 className="text-sm font-semibold text-gray-800">
                      Sales Reps Reporting to this ZBM
                    </h3>
                    <span className="text-xs text-gray-400">({reports.length} selected)</span>
                  </div>
                  <div className="space-y-1">
                    {allSalesReps.map(rep => {
                      const selected = reports.includes(rep.id);
                      return (
                        <button
                          key={rep.id}
                          onClick={() => toggleReport(rep.id)}
                          className={`w-full flex items-center space-x-2.5 px-3 py-2 rounded-lg text-sm text-left border transition-colors ${
                            selected
                              ? 'bg-indigo-50 border-indigo-300 text-indigo-800'
                              : 'bg-white border-gray-200 text-gray-700 hover:border-indigo-300'
                          }`}
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                            selected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'
                          }`}>
                            {selected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{rep.name}</p>
                            <p className="text-xs text-gray-500">{rep.email}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Hospital Assignment (Zone-wise) ─────────────────────── */}
              {(isSales || isPhysician) && (
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <Building2 className="w-4 h-4 text-emerald-500" />
                    <h3 className="text-sm font-semibold text-gray-800">
                      Hospital Assignment (Zone-wise)
                    </h3>
                    <span className="text-xs text-gray-400">
                      ({allSelectedNames.size} selected)
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mb-3">
                    {isSales
                      ? 'Select zones to assign — hospitals for that zone will appear below for explicit selection.'
                      : 'Select hospitals assigned to this physician.'}
                  </p>

                  {/* Search box — Physician only */}
                  {isPhysician && (
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search hospitals..."
                        autoComplete="off"
                        value={hospitalSearch}
                        onChange={e => setHospitalSearch(e.target.value)}
                        className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                      {hospitalSearch && (
                        <button
                          onClick={() => setHospitalSearch('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}

                  {/* Zone selector — Sales only */}
                  {isSales && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {/* All Zones button — ZBM only */}
                      {isZBM && (
                        <button
                          onClick={toggleAllZones}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                            zones.length === 0
                              ? 'bg-gray-800 text-white border-gray-800'
                              : 'bg-white text-gray-500 border-gray-300 hover:border-gray-500'
                          }`}
                        >
                          All Zones
                        </button>
                      )}
                      {allZones.map(zone => (
                        <button
                          key={zone}
                          onClick={() => toggleZone(zone)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                            zones.includes(zone)
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                          }`}
                        >
                          {zone}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Hospital list */}
                  <div className="space-y-3">
                    {filteredHospitalsByZone.length === 0 ? (
                      <div className="text-center py-6 text-gray-400">
                        <Building2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p className="text-xs">
                          {isSales && zones.length === 0
                            ? isZBM
                              ? 'Select "All Zones" or a specific zone to see hospitals'
                              : 'Select a zone above to see hospitals'
                            : isPhysician && hospitalSearch
                              ? `No hospitals found for "${hospitalSearch}"`
                              : 'No hospitals available for selected zone(s)'}
                        </p>
                      </div>
                    ) : (
                      filteredHospitalsByZone.map(([zone, hosps]) => (
                        <div key={zone}>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                            {zone}
                          </p>
                          <div className="space-y-1">
                            {hosps.map(h => {
                              const isOwn     = !!hospitals.find(x => x.name === h.name);
                              const isRepOnly = repSourcedNames.has(h.name) && !isOwn;
                              const isSelected = allSelectedNames.has(h.name);
                              const sourceReps = isRepOnly
                                ? allSalesReps.filter(rep =>
                                    repHospitals[rep.id]?.some(rh => rh.name === h.name))
                                : [];

                              return (
                                <button
                                  key={h.name}
                                  onClick={() => !isRepOnly && toggleHospital(h)}
                                  disabled={isRepOnly}
                                  className={`w-full flex items-center space-x-2.5 px-3 py-2 rounded-lg text-sm text-left border transition-colors ${
                                    isSelected
                                      ? isRepOnly
                                        ? 'bg-indigo-50 border-indigo-200 text-indigo-700 cursor-not-allowed'
                                        : 'bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100'
                                      : 'bg-white border-gray-200 text-gray-700 hover:border-emerald-300 hover:bg-emerald-50'
                                  }`}
                                >
                                  <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                    isSelected
                                      ? isRepOnly ? 'bg-indigo-500 border-indigo-500' : 'bg-emerald-600 border-emerald-600'
                                      : 'border-gray-300'
                                  }`}>
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                  </div>
                                  <span className="truncate flex-1">{h.name}</span>
                                  {isRepOnly && sourceReps.length > 0 && (
                                    <div className="flex items-center space-x-1 text-xs text-indigo-600">
                                      <Lock className="w-3 h-3" />
                                      <span className="font-medium">{sourceReps[0]?.name.split(' ')[0]}</span>
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))
                    )}

                    {/* Legend — ZBM only */}
                    {isZBM && allSelectedNames.size > 0 && (
                      <div className="flex items-center space-x-4 mt-2 pt-2 border-t border-gray-100">
                        <div className="flex items-center space-x-1.5">
                          <div className="w-3 h-3 rounded bg-emerald-600" />
                          <span className="text-xs text-gray-500">Own hospital</span>
                        </div>
                        <div className="flex items-center space-x-1.5">
                          <div className="w-3 h-3 rounded bg-indigo-500" />
                          <Lock className="w-3 h-3 text-indigo-500" />
                          <span className="text-xs text-gray-500">From Sales Rep (auto)</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* No assignments needed */}
              {!isSales && !isPhysician && (
                <div className="text-center py-8 text-gray-400">
                  <Building2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No assignments needed for this role</p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex-shrink-0">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-white transition-colors">
            Cancel
          </button>
          {(isSales || isPhysician) && (
            <button onClick={handleSave} disabled={saving || loading}
              className="flex items-center space-x-2 px-5 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors">
              {saving
                ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <Check className="w-4 h-4" />
              }
              <span>Save Assignments</span>
            </button>
          )}
        </div>

      </div>
    </div>,
    document.body
  );
}
